import { FC, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { getNiceNumber, JsonObject } from '@/lib/helpers';
import CopyClipButton from '@/components/copy-clip-button';
import NiceDate from '@/components/nice-date';
import { getHostname } from '@/lib/url-helpers';
import NoneChip from '@/components/chip/none-chip';
import ConnectorLink from '@/components/connector-link';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import Tooltip from '@/components/tooltip';

interface Props {
  data: UserEventModel;
}

const HostnameWithTooltip: FC<{ url: string }> = ({ url }) => {
  const host = getHostname(url);

  if (!host || host === url) {
    return url;
  }

  return <Tooltip title={url}>{host}</Tooltip>;
};

const RequestDetails: FC<Props> = ({ data }) => {
  const [details, fields] = useMemo(() => {
    if (!data) {
      return [{}, []];
    }

    const {
      startTime,
      endTime,
      compTraceId,
      requestType,
      requestUrl,
      referrer,
      clientIp,
      clientIdentifiers,
      connectors,
      conversationId,
    } = data;

    const { remoteIp, internalIp } = clientIp;
    const { userAgent } = clientIdentifiers;
    const { ingress, inference } = connectors;

    const durationMs = endTime - startTime;

    const fields: ReadonlyField[] = [
      { label: 'Request Type', prop: 'requestType' },
      { label: 'Request Host', prop: 'requestUrl' },
      { label: 'Referrer', prop: 'referrer' },
      { label: 'Start Time', prop: 'startTime', rowGroup: 'time' },
      { label: 'End Time', prop: 'endTime', rowGroup: 'time' },
      { label: 'Duration', prop: 'duration' },
      { label: 'Public Source IP Address', prop: 'remoteIp', rowGroup: 'clientIp' },
      { label: 'Private Source IP Address', prop: 'localIp', rowGroup: 'clientIp' },
      { label: 'User Agent', prop: 'userAgent' },
    ];

    const details: JsonObject = {
      requestType,
      requestUrl: requestUrl ? <HostnameWithTooltip url={requestUrl} /> : <NoneChip />,
      referrer: referrer ? <HostnameWithTooltip url={referrer} /> : <NoneChip notAvailable />,
      startTime: (
        <Stack direction="row" gap={1}>
          <NiceDate val={startTime} withTime withOffset />
        </Stack>
      ),
      endTime: (
        <Stack direction="row" gap={1}>
          <NiceDate val={endTime} withTime withOffset />
        </Stack>
      ),
      duration:
        durationMs < 1000 ? `${durationMs}ms` : `${getNiceNumber(Math.floor(durationMs / 1000))}s`,
      remoteIp: remoteIp || <NoneChip notAvailable />,
      localIp: internalIp || <NoneChip notAvailable />,
      userAgent: userAgent || <NoneChip notAvailable />,
    };

    if (ingress) {
      fields.push({ label: 'Ingress Connector', prop: 'ingressConnector' });
      details.ingressConnector = <ConnectorLink connector={ingress} variant="outlined" showType />;
    }

    if (inference) {
      fields.push({ label: 'Private Model Connector', prop: 'inferenceConnector' });
      details.inferenceConnector = (
        <ConnectorLink connector={inference} variant="outlined" showType />
      );
    }

    fields.push({ label: 'Trace ID', prop: 'traceId' });
    details.traceId = (
      <Stack direction="row" gap={1}>
        <Box>{compTraceId}</Box>
        <CopyClipButton value={compTraceId} tooltip="Copy Trace ID" type="resource-id" />
      </Stack>
    );

    fields.push({ label: 'Conversation ID', prop: 'conversationId' });
    details.conversationId = conversationId ? (
      <Stack direction="row" gap={1}>
        <Box>{conversationId}</Box>
        <CopyClipButton value={conversationId} tooltip="Copy Conversation ID" type="resource-id" />
      </Stack>
    ) : (
      <NoneChip notAvailable />
    );

    return [details, fields];
  }, [data]);

  return <ReadonlyView fields={fields} data={details} />;
};

export default RequestDetails;
